/**
 * "New Organization → Vendor only" (requirements p.1 Vendor account; gap list §3; review PAGES-3).
 * Creates a vendor-only organization and its vendor details in one server step (create_vendor_organization,
 * administrators). The record has no facility type, Facility/Affiliate ID or physical address, so it is never listed
 * on maps, the voucher locator or redemption lists. Organization email/phone are taken from the vendor fields.
 */

import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import {
  createVendorOrganization,
  describeVendorError,
  validateVendorFields,
} from "../../services/organizationVendor.service";
import type { NewVendorOrganization } from "../../services/organizationVendor.service";
import { QUERY_KEYS } from "../../utils/constants";

interface Props {
  onCreated: (id: string) => void;
  onCancel: () => void;
}

const VendorCreateForm: React.FC<Props> = ({ onCreated, onCancel }) => {
  const queryClient = useQueryClient();
  const [f, setF] = useState<NewVendorOrganization>({
    name: "",
    legal_name: "",
    vendor_name: "",
    vendor_number: "",
    vendor_email: "",
    vendor_phone: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createVendorOrganization(f),
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] });
      queryClient.invalidateQueries({ queryKey: ["vendor-index"] });
      onCreated(id);
    },
    onError: (e: Error) => setServerError(describeVendorError(e.message)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const errs: Record<string, string> = { ...validateVendorFields(f) };
    if (!f.name.trim() && !f.vendor_name.trim()) {
      errs.name = "Business name is required";
    }
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      create.mutate();
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={submit}
      noValidate
      data-testid="vendor-create-form"
    >
      <p className="text-sm text-gray-600">
        A vendor-only record has no facility type or physical address and is
        never shown on maps, the voucher locator or redemption lists. If this
        business is also a partner location, choose “Business + Vendor”.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Business Name"
          required
          value={f.name}
          error={errors.name}
          onChange={(e) =>
            setF({
              ...f,
              name: e.target.value,
              vendor_name:
                f.vendor_name === f.name ? e.target.value : f.vendor_name,
            })
          }
        />
        <Input
          label="Legal Name"
          value={f.legal_name}
          onChange={(e) => setF({ ...f, legal_name: e.target.value })}
        />
        <Input
          label="Vendor Name"
          required
          value={f.vendor_name}
          error={errors.vendor_name}
          onChange={(e) => setF({ ...f, vendor_name: e.target.value })}
        />
        <Input
          label="Vendor Number"
          required
          value={f.vendor_number}
          error={errors.vendor_number}
          onChange={(e) => setF({ ...f, vendor_number: e.target.value })}
        />
        <Input
          label="Vendor Email"
          type="email"
          required
          value={f.vendor_email}
          error={errors.vendor_email}
          onChange={(e) => setF({ ...f, vendor_email: e.target.value })}
        />
        <Input
          label="Vendor Phone"
          type="tel"
          required
          value={f.vendor_phone}
          error={errors.vendor_phone}
          onChange={(e) => setF({ ...f, vendor_phone: e.target.value })}
        />
      </div>
      {serverError && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {serverError}
        </div>
      )}
      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={create.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" isLoading={create.isPending}>
          Create vendor
        </Button>
      </div>
    </form>
  );
};

export default VendorCreateForm;
